'use client';
import React, { useEffect, useId, useState } from 'react';
import Link from 'next/link';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { JOURNEY_STAGES, formatSessionDate, registerForWebinar } from '@/lib/webinar/webinarService';

interface WebinarFormProps {
  upcoming: string[];
}

export default function WebinarForm({ upcoming }: WebinarFormProps) {
  const uid = useId();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sessionDate, setSessionDate] = useState(upcoming[0] ?? '');
  const [stage, setStage] = useState('');
  const [question, setQuestion] = useState('');
  const [everyWeek, setEveryWeek] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const [website, setWebsite] = useState('');
  const [source, setSource] = useState('webinar-page');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [bookedDate, setBookedDate] = useState<string | null>(null);

  useEffect(() => {
    try {
      const param = new URLSearchParams(window.location.search).get('source')?.trim();
      if (param) setSource(param.slice(0, 60));
    } catch {
      // keep default source
    }
  }, []);

  useEffect(() => {
    if (!sessionDate && upcoming[0]) setSessionDate(upcoming[0]);
  }, [upcoming, sessionDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const next: { name?: string; email?: string } = {};
    if (!fullName.trim()) next.name = 'Enter your first name and surname';
    if (!email.trim()) next.email = 'Enter your email address';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'Enter a valid email address';
    setErrors(next);
    setError(null);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      const result = await registerForWebinar({
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        sessionDate,
        everyWeek,
        businessStage: stage,
        question: question.trim(),
        source,
        marketingConsent: marketing,
        website,
      });
      setBookedDate(result.sessionDate || sessionDate);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (bookedDate !== null) {
    return (
      <div role="status" className="rounded-2xl bg-[#F8F3E9] p-5 text-[#581C32] sm:p-7">
        <div className="flex items-start gap-3">
          <CheckCircle2 size={26} className="mt-0.5 flex-shrink-0 text-[#C6A667]" aria-hidden="true" />
          <div>
            <h3 className="text-xl font-extrabold">You&apos;re booked!</h3>
            <p className="mt-2 text-base leading-relaxed text-[#2B0D18]">
              Check your inbox (and your spam folder) for your Zoom link and calendar invite. See you on{' '}
              <strong>{formatSessionDate(bookedDate)}</strong>.
            </p>
          </div>
        </div>
        <Link
          href="/start?source=webinar"
          className="mt-5 inline-flex min-h-[52px] w-full items-center justify-center rounded-xl bg-[#581C32] px-6 py-3 text-center text-base font-bold text-[#F8F3E9] shadow-md transition-all hover:brightness-110 active:scale-95 sm:w-auto"
        >
          While you wait: start Course 1 for £49
        </Link>
      </div>
    );
  }

  const labelCls = 'mb-1.5 block text-sm font-semibold text-[#581C32]';
  const inputCls =
    'block w-full min-h-[48px] rounded-lg border border-[#C6A667] bg-white px-3.5 py-2.5 text-base text-[#2B0D18] placeholder:text-[#2B0D18]/40 focus:outline-none focus:ring-2 focus:ring-[#C6A667]';
  const errCls = 'mt-1.5 text-xs font-medium text-destructive';

  return (
    <form onSubmit={handleSubmit} noValidate className="relative space-y-4">
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
      >
        <label htmlFor={`${uid}-website`}>Website</label>
        <input
          id={`${uid}-website`}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor={`${uid}-name`} className={labelCls}>
          First name and surname
        </label>
        <input
          id={`${uid}-name`}
          type="text"
          autoComplete="name"
          required
          aria-required="true"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className={inputCls}
        />
        {errors.name && <p className={errCls}>{errors.name}</p>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${uid}-email`} className={labelCls}>
            Email
          </label>
          <input
            id={`${uid}-email`}
            type="email"
            autoComplete="email"
            required
            aria-required="true"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>
        <div>
          <label htmlFor={`${uid}-phone`} className={labelCls}>
            Phone <span className="font-normal text-[#2B0D18]/60">(optional)</span>
          </label>
          <input
            id={`${uid}-phone`}
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${uid}-date`} className={labelCls}>
            Which Wednesday?
          </label>
          <select
            id={`${uid}-date`}
            value={sessionDate}
            onChange={(e) => setSessionDate(e.target.value)}
            className={inputCls}
          >
            {upcoming.map((d) => (
              <option key={d} value={d}>
                {formatSessionDate(d)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`${uid}-stage`} className={labelCls}>
            Where are you in your journey?
          </label>
          <select id={`${uid}-stage`} value={stage} onChange={(e) => setStage(e.target.value)} className={inputCls}>
            <option value="">Choose one…</option>
            {JOURNEY_STAGES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor={`${uid}-question`} className={labelCls}>
          Anything you&apos;d like Linda to cover? <span className="font-normal text-[#2B0D18]/60">(optional)</span>
        </label>
        <textarea
          id={`${uid}-question`}
          rows={3}
          maxLength={2000}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          className={`${inputCls} resize-y`}
        />
      </div>

      <div className="space-y-3">
        <label htmlFor={`${uid}-weekly`} className="flex cursor-pointer items-start gap-3 text-sm text-[#2B0D18]">
          <input
            id={`${uid}-weekly`}
            type="checkbox"
            checked={everyWeek}
            onChange={(e) => setEveryWeek(e.target.checked)}
            className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-input accent-[#581C32]"
          />
          <span>Remind me every week</span>
        </label>
        <label htmlFor={`${uid}-marketing`} className="flex cursor-pointer items-start gap-3 text-sm text-[#2B0D18]">
          <input
            id={`${uid}-marketing`}
            type="checkbox"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
            className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-input accent-[#581C32]"
          />
          <span>Send me tips, updates and offers from Care Business School by email</span>
        </label>
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive">
          {error}
        </p>
      )}

      <div>
        <button
          type="submit"
          disabled={submitting || !sessionDate}
          className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#581C32] px-6 py-3 text-base font-bold text-[#F8F3E9] shadow-md transition-all hover:brightness-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="animate-spin" aria-hidden="true" /> Registering…
            </>
          ) : (
            'Register free'
          )}
        </button>
        <p className="mt-3 text-xs leading-relaxed text-[#2B0D18]/75">
          We&apos;ll email you the Zoom link straight away. By registering you agree to our{' '}
          <Link href="/privacy-policy" className="font-semibold underline underline-offset-2">
            Privacy Notice
          </Link>
          .
        </p>
      </div>
    </form>
  );
}
